/**
 * TurnSpec — frontend mirror of the bridge's turn-spec.js (defense in depth:
 * the bridge already validated; we re-parse before rendering so a malformed
 * payload can never crash the page).
 */
import { z } from 'zod';
import type { DrillView } from '../components/analytics/drillView';

const FilterConditionSchema = z.object({
  key: z.string().min(1).max(40),
  op: z.enum(['>=', '<=', '==', 'contains']),
  value: z.union([z.string().max(120), z.number()]),
});

const ViewRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('inbox'), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('overview'), title: z.string().max(80).optional() }),
  z.object({
    kind: z.literal('customers'),
    type: z.enum(['receivable', 'payable']),
    bucketDaysFrom: z.number().int().min(0).max(365).optional(),
    filters: z.array(FilterConditionSchema).max(4).optional(),
    title: z.string().max(80).optional(),
  }),
  z.object({ kind: z.literal('customer'), personId: z.number().int().positive(), personName: z.string().max(120).optional(), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('invoice'), invoiceId: z.number().int().positive(), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('journal'), journalEntryId: z.number().int().positive(), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('findings'), scanner: z.string().max(60).optional(), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('bank'), title: z.string().max(80).optional() }),
  z.object({ kind: z.literal('vat'), title: z.string().max(80).optional() }),
]);

const SuggestionSchema = z.object({
  label: z.string().min(1).max(60),
  prompt: z.string().max(200).optional(),
  link: z.string().regex(/^\/[a-z0-9/_-]*$/i).optional(),
});

export const TurnSchema = z.object({
  text: z.string().default(''),
  views: z.array(z.object({
    view: ViewRefSchema,
    datasets: z.array(z.object({
      dataRef: z.string(),
      data: z.unknown(),
    })),
  })).default([]),
  suggestions: z.array(SuggestionSchema).default([]),
});

export type TurnSuggestion = z.infer<typeof SuggestionSchema>;
export type Turn = z.infer<typeof TurnSchema>;

/** Parse the canonical `turn` SSE event; null → the page shows an error card. */
export function parseTurnEvent(payload: unknown): Turn | null {
  const result = TurnSchema.safeParse(payload);
  return result.success ? result.data : null;
}

export type { DrillView };
